/**
 * Step 2: Requirements Component
 * 
 * Enhanced job post requirements form with:
 * - Auto-save via React Hook Form
 * - AI-generated data pre-fill
 * - Must-match grouping
 * - Real-time validation
 * - Progress tracking
 * - Card layout with scrollable content
 * 
 * UI identical to V2, form management uses React Hook Form
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Card, Alert, Button, Typography, message, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useFormContext } from 'react-hook-form';
import { JobPostFormData, RequirementsHistoryEntry } from '../../schemas/jobPostValidation';
import { validateStep2Complete } from '../../hooks/useFormValidation';
import RequirementsList from './components/RequirementsList';
import AddRequirementModal from './components/AddRequirementModal';
import { RequirementState, transformStoreToStep2, transformStep2ToStore } from './utils/formTransformers';
import StepFooter from '../../components/StepFooter';
import { PersistenceStatus } from '../../hooks/useFormPersistence';
import { useRequirementsGeneration } from './hooks/useRequirementsGeneration';
import { buildRefinementContext } from './utils/contextBuilder';
import { generateRequirementsSummary } from './utils/summaryGenerator';
import RequirementsHistoryDrawer from './components/RequirementsHistoryDrawer';
import './index.scss';

interface Step2RequirementsProps {
  currentStep: number;
  totalSteps: number;
  prompt: string;
  isGenerating: boolean;
  onPromptChange: (value: string) => void;
  onGenerate: () => void;
  onPrevious: () => void;
  onNext: () => void;
  isPreviousDisabled?: boolean;
  isNextDisabled?: boolean;
  stepTitles: string[];
  isNavigating?: boolean;
  saveStatus: PersistenceStatus;
}

const Step2Requirements: React.FC<Step2RequirementsProps> = ({
  currentStep,
  totalSteps,
  prompt,
  isGenerating,
  onPromptChange,
  onGenerate,
  onPrevious,
  onNext,
  isPreviousDisabled = false,
  isNextDisabled = false,
  stepTitles,
  isNavigating = false,
  saveStatus,
}) => {
  const { watch, setValue } = useFormContext<JobPostFormData>();
  
  // Requirements generation hook
  const { generateRequirements, isGenerating: isGeneratingRequirements } = useRequirementsGeneration();
  
  // Watch form values
  const formValues = watch();
  const step2Data = formValues.step2;
  const mode = formValues.mode as 'new' | 'edit' | 'clone';
  const isEditMode = mode === 'edit';
  
  // Convert form data to RequirementState for local management
  const requirements = useMemo(() => {
    if (!step2Data || !step2Data.requirements) {
      return [];
    }
    return transformStoreToStep2(step2Data);
  }, [step2Data]);
  
  // Track original requirement IDs when loading in edit mode
  const [originalRequirementIds, setOriginalRequirementIds] = useState<Set<string | number>>(new Set());
  
  // Initialize original requirement IDs when in edit mode and requirements are first loaded
  useEffect(() => {
    if (isEditMode && requirements.length > 0 && originalRequirementIds.size === 0) {
      // Capture the IDs of requirements that exist when first loaded in edit mode
      const initialIds = new Set(requirements.map(req => req.id));
      setOriginalRequirementIds(initialIds);
    }
  }, [isEditMode, requirements, originalRequirementIds.size]);

  // Calculate completion state for validation
  const isComplete = useMemo(() => {
    return validateStep2Complete(step2Data);
  }, [step2Data]);

  // Calculate must-match requirements count
  const mustMatchCount = useMemo(() => {
    return requirements.filter(req => req.mustMatch).length;
  }, [requirements]);

  // Local UI state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState<RequirementState | null>(null);
  const [isHistoryDrawerVisible, setIsHistoryDrawerVisible] = useState(false);

  // Handle AI requirements generation with context-aware refinement and smart merge
  const handleGenerateRequirements = async () => {
    if (!prompt?.trim()) {
      message.warning('Please enter a prompt to refine requirements');
      return;
    }

    if (prompt.trim().length < 10) {
      message.warning('Prompt must be at least 10 characters long');
      return;
    }

    try {
      // Build context from form data
      const context = buildRefinementContext(formValues);
      
      // Get current requirements for smart merge
      const currentRequirements = step2Data?.requirements || [];
      
      // Save requirements before refinement for history
      const requirementsBefore = [...currentRequirements];
      
      // Call generation with context and current requirements
      const result = await generateRequirements(prompt, context, currentRequirements);
      
      if (result && result.requirements && result.requirements.length > 0) {
        // Generate summary of changes
        const action = currentRequirements.length > 0 ? 'refine' : 'generate';
        let summary = '';
        try {
          summary = await generateRequirementsSummary(
            requirementsBefore,
            result.requirements,
            prompt.trim(),
            action
          );
        } catch (error) {
          console.error('Error generating summary:', error);
          // Continue without summary if generation fails
        }

        // Create history entry
        const historyEntry: RequirementsHistoryEntry = {
          id: `refine_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          timestamp: Date.now(),
          prompt: prompt.trim(),
          requirementsBefore,
          requirementsAfter: result.requirements,
          action,
          summary: summary || undefined,
        };
        
        // Get existing history or initialize empty array
        const existingHistory = step2Data?.history || [];
        
        // Add new history entry (limit to last 15 entries to manage localStorage size)
        const updatedHistory = [...existingHistory, historyEntry].slice(-15);
        
        // Update form state with merged requirements and history
        const updatedStep2Data = {
          requirements: result.requirements,
          additionalRequirements: step2Data?.additionalRequirements || [],
          history: updatedHistory,
        };
        
        setValue('step2', updatedStep2Data, { shouldDirty: true });
        
        // Clear the prompt after successful generation
        onPromptChange('');
      }
    } catch (error) {
      // Error handling is done in the hook
      console.error('Error generating requirements:', error);
    }
  };

  // Handle requirements change and save to form
  const handleRequirementsChange = (newRequirements: RequirementState[]) => {
    const step2StoreData = transformStep2ToStore(newRequirements);
    setValue('step2', step2StoreData, { shouldDirty: true });
  };

  // Handle add requirement
  const handleAddRequirement = () => {
    if (isGeneratingRequirements) {
      return;
    }
    setEditingRequirement(null);
    setIsModalVisible(true);
  };

  // Handle edit requirement
  const handleEditRequirement = (requirement: RequirementState) => {
    if (isGeneratingRequirements) {
      return;
    }
    setEditingRequirement(requirement);
    setIsModalVisible(true);
  };

  // Handle delete requirement
  const handleDeleteRequirement = (id: string | number) => {
    if (isGeneratingRequirements) {
      return;
    }
    const newRequirements = requirements.filter((req) => req.id !== id);
    // Update order indices
    const updatedRequirements = newRequirements.map((req, index) => ({
      ...req,
      order: index,
    }));
    handleRequirementsChange(updatedRequirements);
    message.success('Requirement deleted');
  };

  // Handle toggle must-match
  const handleToggleMustMatch = (id: string | number, mustMatch: boolean) => {
    if (isGeneratingRequirements) {
      return;
    }
    
    // Helper function to compare IDs robustly (handles string/number type mismatches)
    const idsMatch = (id1: string | number, id2: string | number): boolean => {
      if (typeof id1 === 'number' && typeof id2 === 'number') {
        return id1 === id2;
      }
      if (typeof id1 === 'string' && typeof id2 === 'string') {
        return id1 === id2;
      }
      // Handle mixed types by converting both to strings for comparison
      return String(id1) === String(id2);
    };
    
    // Find the requirement by ID with robust comparison
    const requirementToUpdate = requirements.find((req) => idsMatch(req.id, id));
    
    if (!requirementToUpdate) {
      console.warn(`Requirement with id ${id} not found. Available IDs:`, requirements.map(r => r.id));
      return;
    }
    
    // Update only the specific requirement by ID (not by index, to avoid index-based issues)
    const newRequirements = requirements.map((req) => {
      return idsMatch(req.id, id) ? { ...req, mustMatch } : req;
    });
    
    handleRequirementsChange(newRequirements);
  };

  // Handle restore from history
  const handleRestoreFromHistory = (entry: RequirementsHistoryEntry) => {
    // Create a new history entry for the restore action
    const restoreEntry: RequirementsHistoryEntry = {
      id: `restore_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      prompt: `Restored from: ${entry.prompt}`,
      requirementsBefore: step2Data?.requirements || [],
      requirementsAfter: entry.requirementsAfter,
      action: 'refine',
    };

    // Get existing history or initialize empty array
    const existingHistory = step2Data?.history || [];
    
    // Add restore entry to history (limit to last 15 entries)
    const updatedHistory = [...existingHistory, restoreEntry].slice(-15);

    // Update form state with restored requirements
    const updatedStep2Data = {
      requirements: entry.requirementsAfter,
      additionalRequirements: step2Data?.additionalRequirements || [],
      history: updatedHistory,
    };

    setValue('step2', updatedStep2Data, { shouldDirty: true });
    message.success('Requirements restored from history');
  };

  // Handle modal submit
  const handleModalSubmit = (requirement: RequirementState) => {
    if (editingRequirement) {
      // Update existing requirement
      const newRequirements = requirements.map((req) =>
        req.id === requirement.id ? requirement : req
      );
      handleRequirementsChange(newRequirements);
      message.success('Requirement updated');
    } else {
      // Add new requirement
      const newRequirement: RequirementState = {
        ...requirement,
        order: requirements.length,
      };
      const newRequirements = [...requirements, newRequirement];
      handleRequirementsChange(newRequirements);
      message.success('Requirement added');
    }
    setIsModalVisible(false);
    setEditingRequirement(null);
  };

  return (
    <div className="step2-container">
      {/* Container-level loader overlay */}
      {isGeneratingRequirements && (
        <div className="step2-loader-overlay">
          <Spin size="large" tip="Generating requirements... Please wait.">
            <div style={{ minHeight: '200px' }} />
          </Spin>
        </div>
      )}
      <Card
        className={`step2-card ${isGeneratingRequirements ? 'step2-card-loading' : ''}`}
        title={
          <div>
            <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 4, fontWeight: 600 }}>
              Job Requirements
            </Typography.Title>
            <Typography.Text type="secondary">
              Define the skills, experience, and qualifications needed for this role.
            </Typography.Text>
          </div>
        }
      >
        {/* Scrollable Card Body */}
        <div className="step2-card-body">
          {/* Show validation error if no must-match requirements */}
          {requirements.length > 0 && !isComplete && (
            <Alert
              message="At least one 'Must Match' requirement is required"
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Requirements List */}
          <RequirementsList
            requirements={requirements}
            onReorder={() => {}} // No-op since drag and drop removed
            onEdit={handleEditRequirement}
            onDelete={handleDeleteRequirement}
            onToggleMustMatch={handleToggleMustMatch}
            mustMatchCount={mustMatchCount}
            isEditMode={isEditMode}
            originalRequirementIds={originalRequirementIds}
          />

          {/* Add Requirement Section */}
          <Card
            className="add-requirement-card"
            bodyStyle={{ padding: '16px', textAlign: 'center' }}
            hoverable
            onClick={handleAddRequirement}
            style={{ cursor: 'pointer', margin: '16px 0' }}
          >
            <Button
              type="text"
              icon={<PlusOutlined />}
              size="large"
              style={{ 
                border: 'none', 
                background: 'transparent'
              }}
            >
              <Typography.Text type="secondary" style={{ fontSize: 'var(--ant-font-size-base)' }}>
                Add Requirement
              </Typography.Text>
            </Button>
          </Card>
        </div>
        <div className="step2-card-footer">
          <StepFooter
            currentStep={currentStep}
            totalSteps={totalSteps}
            prompt={prompt} // Commented out for this release, to be considered in next release - prompt editor is disabled
            isGenerating={isGeneratingRequirements || isGenerating} // Commented out for this release, to be considered in next release
            onPromptChange={onPromptChange} // Commented out for this release, to be considered in next release
            onGenerate={handleGenerateRequirements} // Commented out for this release, to be considered in next release
            onPrevious={onPrevious}
            onNext={onNext}
            isPreviousDisabled={isPreviousDisabled}
            isNextDisabled={isNextDisabled || isGeneratingRequirements}
            stepTitles={stepTitles}
            isNavigating={isNavigating}
            saveStatus={saveStatus}
            historyCount={step2Data?.history?.length} // Commented out for this release, to be considered in next release
            onHistoryClick={() => {
              if (!isGeneratingRequirements) {
                setIsHistoryDrawerVisible(true);
              }
            }} // Commented out for this release, to be considered in next release
          />
        </div>
      </Card>

      {/* Add/Edit Requirement Modal */}
      <AddRequirementModal
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingRequirement(null);
        }}
        onSubmit={handleModalSubmit}
        editingRequirement={editingRequirement}
        mustMatchCount={mustMatchCount}
      />

      {/* History Drawer */}
      <RequirementsHistoryDrawer
        visible={isHistoryDrawerVisible}
        onClose={() => setIsHistoryDrawerVisible(false)}
        history={step2Data?.history || []}
        onRestore={handleRestoreFromHistory}
        currentRequirementsCount={step2Data?.requirements?.length || 0}
        initialPrompt={formValues.step0?.prompt}
        initialRequirements={
          formValues.step0?.prompt && step2Data?.history && step2Data.history.length > 0
            ? step2Data.history.find(entry => entry.action === 'generate')?.requirementsAfter
            : formValues.step0?.prompt && step2Data?.requirements && step2Data.requirements.length > 0 && (!step2Data.history || step2Data.history.length === 0)
              ? step2Data.requirements // If no history but requirements exist, these are likely initial
              : undefined
        }
      />
    </div>
  );
};

Step2Requirements.displayName = 'Step2Requirements';

export default Step2Requirements;
